import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  isSignInWithEmailLink,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  updatePassword,
  updateProfile,
} from "firebase/auth";
import { createContext, useContext, useEffect, useState } from "react";
import { LoadingSpinner } from "../components/ui/LoadingSpinner";
import API_CONFIG from "../config/api";
import { EMAIL_FOR_SIGN_IN_KEY } from "../constants/auth";
import { ROUTES } from "../constants/routes";
import { auth } from "../firebase/config";
import { signInWithGoogleUnified, nativeSignOut } from "../firebase/nativeAuth";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { identify, track } from "../services/analytics";
import apiService from "../services/api";
import { getFirebaseErrorMessage } from "../utils/firebaseErrorMessages";
import { useToast } from "./ToastContext";

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const { showToast } = useToast();
  const [currentUser, setCurrentUser] = useState(null);

  const { setValue, removeValue } = useLocalStorage("accessToken", null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSignup, setIsSignup] = useState(false);

  // Sign up with email and password
  const signup = async (email, password, firstName, lastName) => {
    setIsSignup(true);
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      // Update Firebase profile with user's name
      if (userCredential.user) {
        await updateProfile(userCredential.user, {
          displayName: `${firstName} ${lastName}`,
        });
        const idToken = await userCredential.user.getIdToken();
        await updateUserProfileInBackend(firstName, lastName, idToken);
        track("sign_up", { method: "email" });

        // Send email verification via backend API
        try {
          await apiService.sendVerificationEmail(idToken);
          track("verification_email_sent");
        } catch (error) {
          showToast("Error sending email verification", "error");
        }

        // Sign out user immediately after registration
        // They must verify email before being able to sign in
        await signOut(auth);
      }

      return userCredential;
    } catch (error) {
      const friendlyMessage = getFirebaseErrorMessage(error);
      setError(friendlyMessage);
      throw error;
    } finally {
      setIsSignup(false);
    }
  };

  // Sign in with email and password
  const signin = async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );

      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        // Sign out immediately if email not verified
        await signOut(auth);
        track("login_blocked_unverified");
        const error = new Error("Please verify your email before signing in");
        error.code = "auth/email-not-verified";
        throw error;
      }

      track("login", { method: "email" });
      return userCredential;
    } catch (error) {
      const friendlyMessage = getFirebaseErrorMessage(error);
      setError(friendlyMessage);
      throw error;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setError(null);
      // CAP-2: native sign-in sheet in the WebView, popup on web.
      const result = await signInWithGoogleUnified();

      track("login", { method: "google" });
      return result;
    } catch (error) {
      const friendlyMessage = getFirebaseErrorMessage(error);
      setError(friendlyMessage);
      throw { ...error, friendlyMessage };
    }
  };

  // Sign out
  const logout = async () => {
    try {
      setError(null);
      removeValue();
      track("logout");
      await nativeSignOut();
      return await signOut(auth);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Send password reset email
  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
      return { success: true, message: "Password reset email sent!" };
    } catch (error) {
      const friendlyMessage = getFirebaseErrorMessage(error);
      setError(friendlyMessage);
      throw { ...error, friendlyMessage };
    }
  };

  // Send magic sign-in link to email
  const sendSignInLink = async (email) => {
    try {
      setError(null);
      const actionCodeSettings = {
        url: `${window.location.origin}${ROUTES.AUTH_ACTION}`,
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
      return { success: true, message: "Sign-in link sent!" };
    } catch (error) {
      const friendlyMessage = getFirebaseErrorMessage(error);
      setError(friendlyMessage);
      throw { ...error, friendlyMessage };
    }
  };

  // Complete sign-in from an emailed magic link
  const completeSignInWithLink = async (email, url) => {
    try {
      setError(null);
      if (!isSignInWithEmailLink(auth, url)) {
        throw { code: "auth/invalid-action-code" };
      }
      const userCredential = await signInWithEmailLink(auth, email, url);
      window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
      return userCredential;
    } catch (error) {
      const friendlyMessage = getFirebaseErrorMessage(error);
      setError(friendlyMessage);
      throw { ...error, friendlyMessage };
    }
  };

  // Resend email verification
  const resendVerificationEmail = async (email, password) => {
    try {
      setError(null);
      // Sign in temporarily to resend verification email
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );

      if (userCredential.user.emailVerified) {
        await signOut(auth);
        return {
          success: false,
          message: "Email is already verified. You can sign in now.",
        };
      }

      // Send email verification via backend API
      const idToken = await userCredential.user.getIdToken();
      await apiService.sendVerificationEmail(idToken);
      await signOut(auth);
      return { success: true, message: "Verification email sent!" };
    } catch (error) {
      const friendlyMessage = getFirebaseErrorMessage(error);
      setError(friendlyMessage);
      throw { ...error, friendlyMessage };
    }
  };

  // Update user profile in backend
  const updateUserProfileInBackend = async (firstName, lastName, token) => {
    try {
      setError(null);

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update profile: ${response.status}`);
      }

      return { success: true, message: "Profile updated in backend!" };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Update user profile
  const updateUserProfile = async (displayName, photoURL) => {
    try {
      setError(null);
      await updateProfile(currentUser, {
        displayName: displayName,
        photoURL: photoURL,
      });
      return { success: true, message: "Profile updated!" };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Update user password
  const changePassword = async (newPassword) => {
    try {
      setError(null);
      await updatePassword(currentUser, newPassword);
      return { success: true, message: "Password updated!" };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Re-authenticate user (required for sensitive operations)
  const reauthenticate = async (password) => {
    try {
      setError(null);
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        password,
      );
      await reauthenticateWithCredential(currentUser, credential);
      return { success: true };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fetch user profile from backend when user changes
  const fetchUserProfile = async (accessToken) => {
    if (!accessToken) return null;

    setValue(accessToken);
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const profile = await response.json();

        setCurrentUser({ ...profile.data, accessToken });
        return profile.data;
      } else {
        const err = await response.json();
        if (!error) {
          setError(err.error);
        }
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  // Глобальний слухач для оновлення профілю після refresh токена
  useEffect(() => {
    const handler = (e) => {
      const token = e.detail?.token;
      if (token) fetchUserProfile(token);
    };
    window.addEventListener("tokenRefreshed", handler);
    return () => window.removeEventListener("tokenRefreshed", handler);
  }, []);

  useEffect(() => {
    // Safety net: in a Capacitor WKWebView, Firebase Auth's initial
    // onAuthStateChanged can be delayed (or never fire against an unreachable
    // project), which would leave the whole app stuck on its loading gate.
    // Stop blocking after a short grace period and treat the user as signed-out;
    // if the listener fires later it corrects the state. (See CAP-2: native auth.)
    const authTimeout = setTimeout(() => setLoading(false), 3000);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      clearTimeout(authTimeout);
      // Fetch user profile if user is authenticated
      if (user?.accessToken && !isSignup && user?.emailVerified) {
        identify(user.uid);
        await fetchUserProfile(user.accessToken);
      } else {
        identify(null);
        setCurrentUser(null);
      }

      setLoading(false);
    });

    return () => {
      clearTimeout(authTimeout);
      unsubscribe();
    };
  }, [isSignup]);

  const value = {
    currentUser,
    fetchUserProfile,
    signup,
    signin,
    signInWithGoogle,
    logout,
    resetPassword,
    sendSignInLink,
    completeSignInWithLink,
    resendVerificationEmail,
    updateUserProfile,
    updateUserProfileInBackend,
    changePassword,
    reauthenticate,
    error,
    setError,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <LoadingSpinner /> : children}
    </AuthContext.Provider>
  );
};
